import { DEDUCTION_CATEGORY_LABELS, type BenefitCategoryCode, type DeductionCategoryCode } from './categories';
import type {
  BenefitCatalogItem, CreatesPayrollDeduction, DeductionCalculationType, DeductionCatalogItem, DeductionClassification,
} from './types';

/**
 * The starter catalogs, transcribed from docs/benefits-deductions-starter-catalog.md (the exact starter list from the product request). One item per
 * bullet, in that order within its category (sortOrder 10, 20, 30, ...), version 1, with the stable codes from the contract (section 2):
 * `ben.<category-code>.<kebab-name>` and `ded.<category-code>.<kebab-name>`. Names that appear in two categories (Adoption Assistance) get one code
 * per category. The real API serves its own copy of these lists; the Mock gateways serve this one, so a demo shows exactly what the server will seed.
 *
 * Per-item defaults follow the catalog document's rules: premiums and employee contributions are `Typical`; employer-paid programs are `Never` when
 * they are a plan description only, otherwise `Optional` (and `Optional` when unsure); compensation-like programs and taxable allowances are `Never`;
 * Leave & Well-Being items are `Never` with `leaveLinked`; "Other ..." items are `Optional` (their custom form opens pre-set to the category).
 */

const CODE_PUNCTUATION = /[()'’]/g;

/** `Traditional 401(k)` -> `traditional-401k`, `Gym or Fitness Benefit` -> `gym-or-fitness-benefit`. */
export function kebab(name: string): string {
  return name
    .toLowerCase()
    .replace(CODE_PUNCTUATION, '')
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type BenefitRow = readonly [name: string, creates: CreatesPayrollDeduction, description: string];

const T: CreatesPayrollDeduction = 'Typical';
const O: CreatesPayrollDeduction = 'Optional';
const N: CreatesPayrollDeduction = 'Never';

const HEALTH_WELLNESS: BenefitRow[] = [
  ['Medical Insurance', T, 'Health coverage for medical care, with the employee premium usually taken from pay.'],
  ['Dental Insurance', T, 'Coverage for dental care, with the employee premium usually taken from pay.'],
  ['Vision Insurance', T, 'Coverage for eye exams and eyewear, with the employee premium usually taken from pay.'],
  ['Prescription Drug Coverage', O, 'Coverage for prescription medicines, offered on its own or as part of a medical plan.'],
  ['Health Savings Account', T, 'A savings account for medical costs that the employee funds from pay.'],
  ['Health Reimbursement Arrangement', N, 'An employer-funded arrangement that repays eligible medical costs. This describes the plan only.'],
  ['Healthcare Flexible Spending Account', T, 'A pre-funded account for medical costs that the employee funds from pay.'],
  ['Limited-Purpose Flexible Spending Account', T, 'A pre-funded account limited to dental and vision costs that the employee funds from pay.'],
  ['Employee Assistance Program', N, 'An employer-paid confidential support service for personal and work concerns.'],
  ['Mental Health Support', N, 'An employer-paid program that gives employees access to mental health support.'],
  ['Telehealth', N, 'An employer-paid program that gives employees access to care by phone or video.'],
  ['Wellness Program', N, 'An employer-paid program that encourages healthy habits.'],
  ['Gym or Fitness Benefit', O, 'A gym membership or fitness allowance, paid by the employer or shared with the employee.'],
  ['Smoking Cessation Program', N, 'An employer-paid program that supports employees who want to quit smoking.'],
  ['Weight Management Program', N, 'An employer-paid program that supports healthy weight goals.'],
  ['Occupational Health Program', N, 'An employer-paid program for workplace health checks and safety.'],
  ['Other Health & Wellness Benefit', O, 'A health or wellness benefit that is not listed here. Opens the custom form set to this category.'],
];

const INSURANCE: BenefitRow[] = [
  ['Employer-Paid Basic Life Insurance', N, 'Basic life insurance paid for by the employer. This describes the plan only.'],
  ['Supplemental Employee Life Insurance', T, 'Extra life insurance for the employee, with the premium usually taken from pay.'],
  ['Spouse Life Insurance', T, 'Life insurance for an employee\'s spouse, with the premium usually taken from pay.'],
  ['Dependent Life Insurance', T, 'Life insurance for an employee\'s children, with the premium usually taken from pay.'],
  ['Accidental Death and Dismemberment', N, 'Employer-paid coverage for loss of life or limb from an accident. This describes the plan only.'],
  ['Short-Term Disability', T, 'Income protection for a short absence from work, with any employee premium taken from pay.'],
  ['Long-Term Disability', T, 'Income protection for a long absence from work, with any employee premium taken from pay.'],
  ['Accident Insurance', T, 'Cash benefits after a covered accident, with the premium usually taken from pay.'],
  ['Critical Illness Insurance', T, 'A cash benefit after a covered serious diagnosis, with the premium usually taken from pay.'],
  ['Hospital Indemnity Insurance', T, 'Cash benefits for covered hospital stays, with the premium usually taken from pay.'],
  ['Business Travel Accident Insurance', N, 'Employer-paid coverage for accidents on business travel. This describes the plan only.'],
  ['Identity Theft Protection', T, 'A service that monitors and helps recover from identity theft, with the cost usually taken from pay.'],
  ['Legal Services Plan', T, 'Access to legal help for personal matters, with the cost usually taken from pay.'],
  ['Pet Insurance', T, 'Voluntary coverage for pet care, with the premium usually taken from pay.'],
  ['Other Insurance Benefit', O, 'An insurance benefit that is not listed here. Opens the custom form set to this category.'],
];

const RETIREMENT_SAVINGS: BenefitRow[] = [
  ['Traditional 401(k)', T, 'A retirement savings plan funded by employee contributions taken from pay before income tax.'],
  ['Roth 401(k)', T, 'A retirement savings plan funded by employee contributions taken from pay after income tax.'],
  ['Safe Harbor 401(k)', T, 'A 401(k) plan with required employer contributions and employee contributions taken from pay.'],
  ['403(b)', T, 'A retirement savings plan for eligible organizations, funded by employee contributions from pay.'],
  ['Governmental 457(b)', T, 'A retirement savings plan for government employers, funded by employee contributions from pay.'],
  ['SIMPLE IRA', T, 'A simple retirement plan for small employers, funded by employee contributions from pay.'],
  ['SEP IRA', O, 'A retirement plan where the employer contributes to employee IRAs.'],
  ['Defined Benefit Pension', O, 'A pension that pays a set benefit in retirement.'],
  ['Cash Balance Plan', O, 'A pension plan that shows each employee a hypothetical account balance.'],
  ['Profit-Sharing Plan', N, 'A retirement plan funded by employer contributions that depend on company results.'],
  ['Employee Stock Ownership Plan', N, 'A plan that gives employees an ownership share in the company.'],
  ['Employer Matching Contribution', N, 'Employer contributions that match what employees put into a retirement plan.'],
  ['Employer Nonelective Contribution', N, 'Employer contributions made to every eligible employee, whether or not they contribute.'],
  ['Student Loan Retirement Match', N, 'Employer retirement contributions based on an employee\'s student loan payments.'],
  ['Retirement Financial Counseling', N, 'An employer-paid service that helps employees plan for retirement.'],
  ['Other Retirement Benefit', O, 'A retirement benefit that is not listed here. Opens the custom form set to this category.'],
];

const FINANCIAL_EQUITY: BenefitRow[] = [
  ['Annual Bonus Plan', N, 'A yearly bonus program. The bonus itself is paid through an approved payroll earning code, not through a deduction.'],
  ['Profit Sharing', N, 'A program that shares company profit with employees.'],
  ['Employee Stock Purchase Plan', O, 'A plan that lets employees buy company stock, often through a contribution taken from pay.'],
  ['Stock Options', N, 'The right to buy company stock at a set price in the future.'],
  ['Restricted Stock Units', N, 'A promise of company stock that is earned over time.'],
  ['Phantom Equity', N, 'A cash award that is based on the value of company shares.'],
  ['Deferred Compensation', N, 'Pay that is earned now and paid out at a later date.'],
  ['Tuition Assistance', N, 'Employer help paying for school or training.'],
  ['Student Loan Repayment Assistance', O, 'Employer help paying down an employee\'s student loans.'],
  ['Financial Wellness Program', O, 'A program that helps employees with budgeting and financial planning.'],
  ['Emergency Savings Contribution', O, 'A savings program for unexpected costs, which can be funded from pay.'],
  ['Adoption Assistance', N, 'Employer help with the cost of adopting a child.'],
  ['Relocation Assistance', N, 'Employer help with the cost of moving for work.'],
  ['Housing Assistance', O, 'Employer help with housing costs.'],
  ['Other Financial Benefit', O, 'A financial benefit that is not listed here. Opens the custom form set to this category.'],
];

const FAMILY_LIFESTYLE: BenefitRow[] = [
  ['Dependent Care Flexible Spending Account', T, 'A pre-funded account for child and adult dependent care that the employee funds from pay.'],
  ['Childcare Assistance', N, 'Employer help paying for childcare.'],
  ['Backup Childcare', O, 'Emergency childcare when regular care falls through.'],
  ['Eldercare Assistance', O, 'Help finding or paying for care for an older family member.'],
  ['Adoption Assistance', N, 'Employer help with the cost of adopting a child.'],
  ['Fertility Benefit', O, 'Coverage or support for fertility care.'],
  ['Parental Support', O, 'Programs that support new and growing families.'],
  ['Commuter Transit Benefit', T, 'A pre-funded account for transit costs that the employee funds from pay.'],
  ['Qualified Parking Benefit', T, 'A pre-funded account for parking costs that the employee funds from pay.'],
  ['Transportation Allowance', N, 'A taxable allowance for getting to work. It is an earning, not a deduction.'],
  ['Meal Allowance', N, 'A taxable allowance for meals. It is an earning, not a deduction.'],
  ['Mobile Phone Allowance', N, 'A taxable allowance for a mobile phone. It is an earning, not a deduction.'],
  ['Internet Allowance', N, 'A taxable allowance for home internet. It is an earning, not a deduction.'],
  ['Remote Work Stipend', N, 'A taxable stipend for working remotely. It is an earning, not a deduction.'],
  ['Education Assistance', O, 'Employer help with education costs that are not covered by tuition assistance.'],
  ['Professional Membership', N, 'Employer-paid dues for a professional association.'],
  ['Certification Reimbursement', N, 'Employer repayment of the cost of a work-related certification.'],
  ['Employee Discount', N, 'Discounts on products or services for employees.'],
  ['Other Family or Lifestyle Benefit', O, 'A family or lifestyle benefit that is not listed here. Opens the custom form set to this category.'],
];

const LEAVE_NOTE = 'Balances and accruals are managed in Time and Leave.';
const LEAVE_WELLBEING: readonly (readonly [name: string, description: string])[] = [
  ['Vacation', `Paid vacation days. ${LEAVE_NOTE}`],
  ['Sick Leave', `Paid time away when an employee is unwell. ${LEAVE_NOTE}`],
  ['Paid Time Off', `A combined bank of paid days away from work. ${LEAVE_NOTE}`],
  ['Paid Family Leave', `Paid time away to care for a family member. ${LEAVE_NOTE}`],
  ['Paid Parental Leave', `Paid time away for a new child. ${LEAVE_NOTE}`],
  ['Bereavement Leave', `Paid time away after the death of a loved one. ${LEAVE_NOTE}`],
  ['Jury Duty Leave', `Paid time away for jury service. ${LEAVE_NOTE}`],
  ['Military Leave', `Time away for military service. ${LEAVE_NOTE}`],
  ['Volunteer Time Off', `Paid time away for volunteering. ${LEAVE_NOTE}`],
  ['Sabbatical', `Extended time away from work. ${LEAVE_NOTE}`],
  ['Other Leave Benefit', `A leave benefit that is not listed here. Opens the custom form set to this category. ${LEAVE_NOTE}`],
];

function benefitItems(category: BenefitCategoryCode, rows: readonly BenefitRow[]): BenefitCatalogItem[] {
  return rows.map(([name, createsPayrollDeduction, description], index) => ({
    code: `ben.${category}.${kebab(name)}`,
    name,
    categoryCode: category,
    description,
    createsPayrollDeduction,
    leaveLinked: false,
    leaveNote: null,
    version: 1,
    sortOrder: (index + 1) * 10,
    isHidden: false,
  }));
}

function leaveItems(): BenefitCatalogItem[] {
  return LEAVE_WELLBEING.map(([name, description], index) => ({
    code: `ben.leave-wellbeing.${kebab(name)}`,
    name,
    categoryCode: 'leave-wellbeing' as const,
    description,
    createsPayrollDeduction: 'Never' as const,
    leaveLinked: true,
    leaveNote: LEAVE_NOTE,
    version: 1,
    sortOrder: (index + 1) * 10,
    isHidden: false,
  }));
}

/** `other-custom` has no starter items: companies create custom benefits there. */
export const BENEFIT_CATALOG: readonly BenefitCatalogItem[] = [
  ...benefitItems('health-wellness', HEALTH_WELLNESS),
  ...benefitItems('insurance', INSURANCE),
  ...benefitItems('retirement-savings', RETIREMENT_SAVINGS),
  ...benefitItems('financial-equity', FINANCIAL_EQUITY),
  ...benefitItems('family-lifestyle', FAMILY_LIFESTYLE),
  ...leaveItems(),
];

/** The one catalog type with a standing rule: the bonus itself goes through an approved payroll earning code, so the plan has no payroll contribution. */
export const ANNUAL_BONUS_PLAN_CODE = 'ben.financial-equity.annual-bonus-plan';
export const ANNUAL_BONUS_NOTE = 'The bonus payment goes through an approved payroll earning code. This plan carries no payroll contribution.';

/** True for a catalog "Other ..." item: it opens the custom form pre-set to its category instead of creating from a fixed type. */
export function isOtherCatalogItem(item: { code: string }): boolean {
  const last = item.code.split('.').pop() ?? '';
  return last.startsWith('other-');
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Deductions
// ---------------------------------------------------------------------------------------------------------------------------------------------

type DeductionRow = readonly [name: string, description: string];

const BENEFIT_PREMIUMS: DeductionRow[] = [
  ['Medical Premium', 'The employee share of a medical plan premium.'],
  ['Dental Premium', 'The employee share of a dental plan premium.'],
  ['Vision Premium', 'The employee share of a vision plan premium.'],
  ['Supplemental Life Premium', 'The employee cost of extra life insurance.'],
  ['Dependent Life Premium', 'The employee cost of life insurance for dependents.'],
  ['Short-Term Disability Premium', 'The employee share of a short-term disability premium.'],
  ['Long-Term Disability Premium', 'The employee share of a long-term disability premium.'],
  ['Accident Insurance Premium', 'The employee cost of accident insurance.'],
  ['Critical Illness Premium', 'The employee cost of critical illness insurance.'],
  ['Hospital Indemnity Premium', 'The employee cost of hospital indemnity insurance.'],
  ['Health Savings Account Contribution', 'The amount an employee puts into a health savings account.'],
  ['Healthcare FSA Contribution', 'The amount an employee puts into a healthcare flexible spending account.'],
  ['Dependent Care FSA Contribution', 'The amount an employee puts into a dependent care flexible spending account.'],
  ['Commuter Transit Contribution', 'The amount an employee puts into a commuter transit account.'],
  ['Qualified Parking Contribution', 'The amount an employee puts into a qualified parking account.'],
  ['Other Benefit Premium', 'A benefit premium that is not listed here. Opens the custom form set to this category.'],
];

const RETIREMENT_CONTRIBUTIONS: DeductionRow[] = [
  ['Traditional 401(k)', 'An employee contribution to a traditional 401(k) plan.'],
  ['Roth 401(k)', 'An employee contribution to a Roth 401(k) plan.'],
  ['401(k) Catch-Up', 'An extra traditional 401(k) contribution for eligible employees.'],
  ['Roth 401(k) Catch-Up', 'An extra Roth 401(k) contribution for eligible employees.'],
  ['403(b)', 'An employee contribution to a 403(b) plan.'],
  ['Roth 403(b)', 'An employee contribution to a Roth 403(b) plan.'],
  ['Governmental 457(b)', 'An employee contribution to a governmental 457(b) plan.'],
  ['SIMPLE IRA', 'An employee contribution to a SIMPLE IRA.'],
  ['Other Approved Retirement Contribution', 'A retirement contribution that is not listed here. Opens the custom form set to this category.'],
  ['Retirement Loan Repayment', 'Repayment of a loan taken from a retirement plan.'],
];

const GARNISHMENTS_LEVIES: DeductionRow[] = [
  ['Child Support', 'A court or agency order to withhold child support.'],
  ['Spousal Support', 'A court order to withhold spousal support.'],
  ['Federal Tax Levy', 'An order from a federal tax authority to withhold pay.'],
  ['State Tax Levy', 'An order from a state tax authority to withhold pay.'],
  ['Creditor Garnishment', 'A court order to withhold pay for a creditor.'],
  ['Student Loan Garnishment', 'An order to withhold pay for a defaulted student loan.'],
  ['Bankruptcy Order', 'A bankruptcy court order to withhold pay.'],
  ['Other Court-Ordered Withholding', 'Another withholding required by a court or agency order.'],
];

const LOANS_REPAYMENTS: DeductionRow[] = [
  ['Employee Loan Repayment', 'Repayment of a loan from the company.'],
  ['Payroll Advance Repayment', 'Repayment of an advance on pay.'],
  ['Wage Overpayment Recovery', 'Recovery of pay that was paid by mistake.'],
  ['Equipment Repayment', 'Repayment for company equipment the employee agreed to pay for.'],
  ['Uniform Repayment', 'Repayment for uniforms the employee agreed to pay for.'],
  ['Education Repayment', 'Repayment of education costs the company paid, under an agreement.'],
  ['Other Authorized Repayment', 'A repayment the employee has authorized that is not listed here.'],
];

const UNION_CHARITABLE_OTHER: DeductionRow[] = [
  ['Union Dues', 'Regular dues paid to a union.'],
  ['Union Initiation Fee', 'A one-time fee to join a union.'],
  ['Charitable Contribution', 'A donation to a charity that the employee chose to give through pay.'],
  ['Political Action Contribution', 'A voluntary contribution to a political action committee.'],
  ['Employee Association Dues', 'Dues paid to an employee association.'],
  ['Professional Membership', 'Dues for a professional association that the employee pays through pay.'],
  ['Voluntary Savings', 'Voluntary savings the employee sets aside from pay.'],
  ['Other Voluntary Deduction', 'A voluntary deduction that is not listed here. Opens the custom form set to this category.'],
];

const TAXES_STATUTORY: DeductionRow[] = [
  ['Federal Income Tax', 'Federal income tax withheld from pay. The system calculates it.'],
  ['Social Security', 'Social Security tax withheld from pay. The system calculates it.'],
  ['Medicare', 'Medicare tax withheld from pay. The system calculates it.'],
  ['Additional Medicare Tax', 'Additional Medicare tax withheld from higher pay. The system calculates it.'],
  ['State Income Tax', 'State income tax withheld from pay. The system calculates it.'],
  ['Local Income Tax', 'Local income tax withheld from pay. The system calculates it.'],
  ['State Disability or Paid Leave Deductions', 'State disability or paid leave contributions withheld from pay. The system calculates them.'],
  ['Other Jurisdiction-Mandated Employee Deductions', 'Other deductions that a jurisdiction requires. The system calculates them.'],
];

function deductionItems(
  category: DeductionCategoryCode,
  rows: readonly DeductionRow[],
  classification: DeductionClassification,
  calculation: (name: string) => DeductionCalculationType | null,
): DeductionCatalogItem[] {
  return rows.map(([name, description], index) => ({
    code: `ded.${category}.${kebab(name)}`,
    name,
    categoryCode: category,
    categoryName: DEDUCTION_CATEGORY_LABELS[category],
    description,
    classification,
    defaultCalculationType: calculation(name),
    isStatutory: classification === 'Statutory',
    isReadOnly: classification === 'Statutory',
    requiresRuleReview: category === 'garnishments-levies',
    version: 1,
    sortOrder: (index + 1) * 10,
    isHidden: false,
  }));
}

/** `other-custom` has no starter items: companies create custom deductions there. */
export const DEDUCTION_CATALOG: readonly DeductionCatalogItem[] = [
  ...deductionItems('benefit-premiums', BENEFIT_PREMIUMS, 'Voluntary', () => 'FixedAmount'),
  ...deductionItems('retirement-contributions', RETIREMENT_CONTRIBUTIONS, 'Voluntary',
    (name) => (name === 'Retirement Loan Repayment' ? 'RemainingBalanceUpToPerPayrollAmount' : 'ApprovedRetirementElectionPercent')),
  ...deductionItems('garnishments-levies', GARNISHMENTS_LEVIES, 'Involuntary', () => 'ApprovedGarnishmentFormula'),
  ...deductionItems('loans-repayments', LOANS_REPAYMENTS, 'Voluntary', () => 'FixedAmount'),
  ...deductionItems('union-charitable-other', UNION_CHARITABLE_OTHER, 'Voluntary', () => 'FixedAmount'),
  ...deductionItems('taxes-statutory', TAXES_STATUTORY, 'Statutory', () => null),
];
